import { FC, useEffect, useRef } from 'react';

import { TotemIconId } from '@meilleurtaux/mtp-uikit-core';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate, generatePath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { RetourPartenaireResponseReturn, usePostApiStandbyFinTunnel } from '@/api';
import { getGetApiArbitragesQueryKey } from '@/api/arbitrage/arbitrage';
import { Loader } from '@/components/Loader/loader';
import TunnelResultPage from '@/components/TunnelResultPage/TunnelResultPage';
import { ROUTE } from '@/constants/route';
import { useQps } from '@/hooks/QpsContext';
import { TRACKING_INSTANCE } from '@/hooks/useTracking/useTrackingInit/useTrackingInit';
import { log } from '@/utils/functions/logger/logger';

const FAMILY_TO_ROUTE: Record<string, string> = {
  ASSURANCEVIE: ROUTE.ASSURANCEVIE,
  DEFISCALISATION: ROUTE.DEFISCALISATION,
  IMMOBILIER: ROUTE.IMMOBILIER,
  RETRAITE: ROUTE.RETRAITE,
  LIVRET: ROUTE.LIVRET,
};

type ResultVariant = 'succes' | 'erreur' | 'erreur_inconnue' | 'refus';

const ROUTE_VARIANT_MAP: Record<string, ResultVariant | undefined> = {
  [ROUTE.ARBITRAGESIGNATURESUCCESS]: 'succes',
  [ROUTE.ARBITRAGESIGNATUREERROR]: 'erreur',
  [ROUTE.ARBITRAGESIGNATUREREFUSAL]: 'refus',
};

const RETURN_TO_VARIANT: Record<RetourPartenaireResponseReturn, ResultVariant> = {
  [RetourPartenaireResponseReturn.success]: 'succes',
  [RetourPartenaireResponseReturn.fail]: 'erreur_inconnue',
  [RetourPartenaireResponseReturn.error]: 'erreur',
};

const SUCCES_RETURN_TO_PAGE_NOM: Record<RetourPartenaireResponseReturn, string> = {
  [RetourPartenaireResponseReturn.success]: 'arbitrage_valide',
  [RetourPartenaireResponseReturn.error]: 'arbitrage_non_valide',
  [RetourPartenaireResponseReturn.fail]: 'arbitrage_non_valide_inconnue',
};

const ROUTE_VARIANT_TO_PAGE_NOM: Partial<Record<ResultVariant, string>> = {
  succes: 'arbitrage_valide',
  erreur: 'arbitrage_non_valide',
  refus: 'refus_d_arbitrage_pris_en_compte',
};

type VariantConfig = {
  iconName: TotemIconId;
  iconVariant: 'success' | 'error';
  title: string;
  subtitles: string[];
  ctaLabel: string;
  secondaryCtaLabel?: string;
};

const ArbitrageSignatureResult: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const id = searchParams.get('placement');
  const family = searchParams.get('family');
  const token = searchParams.get('token');

  const cleanPath = pathname.replace(/\/$/, '');
  const contractRoute = family ? FAMILY_TO_ROUTE[family] : undefined;
  const contractPath = contractRoute && id ? generatePath(contractRoute, { id }) : ROUTE.CONTRATS;
  const recapPath = id ? generatePath(ROUTE.ARBITRAGESUMMARY, { id }) : ROUTE.ERREUR;

  const routeVariant = ROUTE_VARIANT_MAP[cleanPath];
  const queryClient = useQueryClient();
  const { clearQps } = useQps();

  const invalidateArbitrages = () => {
    queryClient.invalidateQueries({ queryKey: getGetApiArbitragesQueryKey() });
  };

  const standbyAfterTunnel = usePostApiStandbyFinTunnel({
    mutation: {
      onSuccess: (data) => {
        if (data.return === RetourPartenaireResponseReturn.success) {
          invalidateArbitrages();
          // Signature confirmée : plus aucune trace des questionnaires. En échec ou en erreur, elles
          // restent pour que le client puisse retenter sans tout refaire.
          clearQps();
        }
        TRACKING_INSTANCE.setTcVars({
          page_nom: SUCCES_RETURN_TO_PAGE_NOM[data.return],
          page_template: 'espace client',
        });
        void TRACKING_INSTANCE.triggerEvent('page', undefined, {
          event_id: 'page',
          tunnel_produit: 'tunnel_unique',
          event_categorie: 'affichage espace client',
          event_nom: SUCCES_RETURN_TO_PAGE_NOM[data.return],
          event_page: SUCCES_RETURN_TO_PAGE_NOM[data.return],
        });
      },
      onError: (error) => {
        log.error(`usePostApiStandbyFinTunnel failed: ${error}`);
        navigate(ROUTE.ERREUR, { replace: true });
      },
    },
  });

  const hasStandbyMutatedRef = useRef(false);

  useEffect(() => {
    if (hasStandbyMutatedRef.current || routeVariant !== 'succes' || !token) return;
    hasStandbyMutatedRef.current = true;
    standbyAfterTunnel.mutate({ token });
  }, [routeVariant, token, hasStandbyMutatedRef]);

  const hasInvalidatedRefusRef = useRef(false);

  useEffect(() => {
    if (hasInvalidatedRefusRef.current || routeVariant !== 'refus') return;
    hasInvalidatedRefusRef.current = true;
    invalidateArbitrages();
  }, [routeVariant]);

  const hasTrackedRefusRef = useRef(false);

  useEffect(() => {
    if (hasTrackedRefusRef.current || !routeVariant || routeVariant === 'succes') return;
    hasTrackedRefusRef.current = true;

    TRACKING_INSTANCE.setTcVars({
      page_nom: ROUTE_VARIANT_TO_PAGE_NOM[routeVariant],
      page_template: 'espace client',
    });
    void TRACKING_INSTANCE.triggerEvent('page', undefined, {
      event_id: 'page',
      tunnel_produit: 'tunnel_unique',
      event_categorie: 'affichage espace client',
      event_nom: ROUTE_VARIANT_TO_PAGE_NOM[routeVariant],
      event_page: ROUTE_VARIANT_TO_PAGE_NOM[routeVariant],
    });
  }, [routeVariant]);

  if (!routeVariant) return <Navigate to={ROUTE.ERREUR} replace />;

  const isPending = routeVariant === 'succes' && (standbyAfterTunnel.isIdle || standbyAfterTunnel.isPending);

  if (isPending) return <Loader />;

  const variant =
    routeVariant === 'succes' && standbyAfterTunnel.data
      ? RETURN_TO_VARIANT[standbyAfterTunnel.data.return]
      : routeVariant;

  const configs: Record<ResultVariant, VariantConfig> = {
    succes: {
      iconName: 'check-circle-solid',
      iconVariant: 'success',
      title: t('arbitrageSignature.success.title'),
      subtitles: [t('arbitrageSignature.success.subtitle')],
      ctaLabel: t('arbitrageSignature.success.cta'),
    },
    erreur: {
      iconName: 'warning-solide',
      iconVariant: 'error',
      title: t('arbitrageSignature.error.title'),
      subtitles: [
        t('arbitrageSignature.error.subtitle1'),
        t('arbitrageSignature.error.subtitle2'),
        t('arbitrageSignature.error.subtitle3'),
      ],
      ctaLabel: t('arbitrageSignature.error.ctaContract'),
      secondaryCtaLabel: t('arbitrageSignature.error.cta'),
    },
    erreur_inconnue: {
      iconName: 'warning-solide',
      iconVariant: 'error',
      title: t('arbitrageSignature.unknown_error.title'),
      subtitles: [t('arbitrageSignature.unknown_error.subtitle1')],
      ctaLabel: t('arbitrageSignature.unknown_error.ctaContract'),
      secondaryCtaLabel: t('arbitrageSignature.unknown_error.cta'),
    },
    refus: {
      iconName: 'check-circle-solid',
      iconVariant: 'success',
      title: t('arbitrageSignature.refusal.title'),
      subtitles: [t('arbitrageSignature.refusal.subtitle')],
      ctaLabel: t('arbitrageSignature.refusal.ctaContract'),
      secondaryCtaLabel: t('arbitrageSignature.refusal.cta'),
    },
  };

  const config = configs[variant];

  return (
    <TunnelResultPage
      iconName={config.iconName}
      iconVariant={config.iconVariant}
      title={config.title}
      subtitles={config.subtitles}
      ctaLabel={config.ctaLabel}
      onCtaClick={() => navigate(contractPath)}
      secondaryCtaLabel={config.secondaryCtaLabel}
      onSecondaryCtaClick={config.secondaryCtaLabel ? () => navigate(recapPath) : undefined}
    />
  );
};

export default ArbitrageSignatureResult;
